import styled from '@emotion/styled';

export type PillVariant = 'blue' | 'green' | 'red' | 'amber' | 'orange' | 'violet' | 'grey';

export interface PillColors {
    color: string;
    bg: string;
    border: string;
    bgHover?: string;
}

const PILL_COLORS: Record<PillVariant, Required<PillColors>> = {
    blue: { color: '#2679ad', bg: '#e1f0f9', border: '#b8d8ea', bgHover: '#c8e2f1' },
    green: { color: '#15803d', bg: '#dcfce7', border: '#bbf7d0', bgHover: '#bbf7d0' },
    red: { color: '#991b1b', bg: '#fee2e2', border: '#fecaca', bgHover: '#fecaca' },
    amber: { color: '#92400e', bg: '#fef3c7', border: '#fde68a', bgHover: '#fde68a' },
    orange: { color: '#9a3412', bg: '#ffedd5', border: '#fed7aa', bgHover: '#fed7aa' },
    violet: { color: '#5b21b6', bg: '#ede9fe', border: '#ddd6fe', bgHover: '#ddd6fe' },
    grey: { color: '#3f3f46', bg: '#f4f4f5', border: '#e4e4e7', bgHover: '#e4e4e7' },
};

// Keyed by ReviewTeam enum member name.
export const REVIEW_TEAM_COLORS: Record<string, PillColors> = {
    CollateralDefense: { color: '#6b21a8', bg: '#f3e8ff', border: '#e9d5ff' },
    Underwriting: { color: '#115e59', bg: '#ccfbf1', border: '#99f6e4' },
    UnderwritingManagers: { color: '#1e40af', bg: '#dbeafe', border: '#bfdbfe' },
    Transitions: { color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
    Sales: { color: '#9d174d', bg: '#fce7f3', border: '#fbcfe8' },
};

interface PillStyleProps {
    variant?: PillVariant;
    colors?: PillColors;
}

const resolveColors = ({ variant = 'blue', colors }: PillStyleProps): Required<PillColors> => {
    if (!colors) return PILL_COLORS[variant];
    return { ...colors, bgHover: colors.bgHover ?? colors.bg };
};

const pillBase = (props: PillStyleProps) => {
    const c = resolveColors(props);
    return `
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        color: ${c.color};
        background: ${c.bg};
        border: 1px solid ${c.border};
    `;
};

export const Pill = styled.span<PillStyleProps>`
    ${(props) => pillBase(props)}
    padding: 9px 10px;
`;

export const CompactPill = styled.span<PillStyleProps>`
    ${(props) => pillBase(props)}
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    line-height: 1.4;
`;

export const PillButton = styled.button<PillStyleProps>`
    ${(props) => pillBase(props)}
    padding: 8px 10px;
    cursor: pointer;
    font-family: inherit;
    &:hover {
        background: ${(props) => resolveColors(props).bgHover};
    }
`;

export const CompactPillButton = styled.button<PillStyleProps>`
    ${(props) => pillBase(props)}
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    line-height: 1.4;
    cursor: pointer;
    font-family: inherit;
    &:hover {
        background: ${(props) => resolveColors(props).bgHover};
    }
`;
